//* 주문 구매자(게스트) 정보 수정 모달 — 구매 시 잘못 입력한 이름/휴대폰번호 교정
//* 게스트 계정(알림톡·문자 수신번호, 간편로그인)과 이 주문의 주문자 표기를 함께 수정한다.
//* (스펙: docs/store-guest-info-update-spec.md)

import { FC, useCallback, useEffect } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { updateShopOrder } from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderListForSellerUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { updateStoreGuestInfo } from '@shared/api/user/user.controller';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import TelField from '@shared/components/TelField';
import { TextField } from '@shared/components/TextField';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from 'react-query';

interface Props {
  openId: string;
  order: ShopOrderForSeller;
}

interface GuestInfoForm {
  name: string;
  phoneNumber: string;
}

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const guestUpdateErrorMessage = (e: unknown): string => {
  switch (statusOf(e)) {
    case 404:
      return '주문을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.';
    case 403:
      return '변경할 수 없는 계정입니다. (게스트 아님 또는 주문-구매자 불일치)';
    case 409:
      return '동일한 정보의 다른 게스트 계정이 이미 있어 변경할 수 없습니다.';
    case 400:
      return '요청이 올바르지 않습니다. (이름/번호 누락 또는 주문에 연결된 구매자 없음)';
    default:
      return '구매자 정보 수정에 실패했습니다.';
  }
};

export const OrderGuestInfoEditModal: FC<Props> = function OrderGuestInfoEditModal({
  openId,
  order,
}: Props) {
  const { sellerRole } = useAuth();
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  const guest = order.user;
  const guestName = guest?.info?.name ?? order.deliveryDetail.senderName;
  const guestPhoneNumber =
    guest?.info?.phoneNumber ?? order.deliveryDetail.senderPhoneNumber;

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  const { register, control, handleSubmit, reset } = useForm<GuestInfoForm>({
    defaultValues: {
      name: guestName,
      phoneNumber: guestPhoneNumber,
    },
  });
  const telValue = useWatch({ control, name: 'phoneNumber', defaultValue: '' });

  //* 열 때마다 최신 게스트 정보로 리시드
  useEffect(() => {
    if (open) {
      reset({
        name: guestName,
        phoneNumber: guestPhoneNumber,
      });
    }
  }, [open, guestName, guestPhoneNumber, reset]);

  const onSubmit: SubmitHandler<GuestInfoForm> = useCallback(
    async (data) => {
      const name = data.name.trim();
      const phoneNumber = String(data.phoneNumber).replace(/[^0-9]/g, '');
      if (!name) {
        notificateError(notifiacationInstance, '이름을 입력해주세요.');
        return;
      }
      if (!/^01[016789][0-9]{7,8}$/.test(phoneNumber)) {
        notificateError(notifiacationInstance, '올바른 휴대폰 번호를 입력해주세요.');
        return;
      }

      const userId = guest?._id;
      if (!userId) {
        notificateError(notifiacationInstance, '주문에 연결된 구매자 정보가 없습니다.');
        return;
      }

      //* 1) 게스트 계정 정보 수정 (알림 수신번호·간편로그인 반영)
      try {
        await updateStoreGuestInfo(
          { shopOrderId: order._id, userId, name, phoneNumber },
          sellerRole(),
        );
      } catch (err) {
        console.error(err);
        notificateError(notifiacationInstance, guestUpdateErrorMessage(err));
        return;
      }

      //* 2) 이 주문의 주문자 표기도 함께 교정 (dot-notation 부분 업데이트)
      try {
        const orderPatch = {
          _id: order._id,
          'deliveryDetail.senderName': name,
          'deliveryDetail.senderPhoneNumber': phoneNumber,
        };
        await updateShopOrder(orderPatch, sellerRole());
      } catch (err) {
        console.error(err);
        notificateError(
          notifiacationInstance,
          '게스트 정보는 수정됐으나 주문서의 주문자 표기 갱신에 실패했습니다.',
        );
      }

      queryClient.invalidateQueries(shopOrderListForSellerUrl(sellerRole()));
      notificateSuccess(notifiacationInstance, '구매자 정보 수정 완료');
      onCloseModal();
    },
    [guest?._id, notifiacationInstance, onCloseModal, order._id, queryClient, sellerRole],
  );

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">구매자 정보 수정</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            구매자가 잘못 입력한 이름/휴대폰번호를 교정합니다. 배송 알림톡·문자
            수신번호와 이 주문의 주문자 표기에 반영됩니다.
          </p>
          <p className="break-keep pt-1 text-xs text-gray-500">
            변경 후 구매자는 새 이름·번호(비밀번호는 기존 그대로)로 주문조회 로그인해야
            합니다.
          </p>
        </div>
        <div className="space-y-3 font-bold">
          <TextField
            className="w-full"
            placeholder="구매자 이름"
            {...register('name', { required: true })}
          />
          <TelField
            value={String(telValue)}
            register={register}
            name="phoneNumber"
            className="w-full"
            required={true}
          />
        </div>
        <Button
          className="button-rectangle h-10 theme-text-4 theme-bg-main"
          onClick={handleSubmit(onSubmit)}
        >
          수정
        </Button>
      </div>
    </Modal>
  );
};
